import { useEffect } from "react";
import { matchPath, useLocation } from "react-router-dom";

const SITE = process.env.REACT_APP_SITE_URL;
const BRAND = "WattNu Ostfriesland";
const DEFAULT_DESC = "Veranstaltungen, Ausflugsziele, Inseln, Orte, Radtouren, Gezeiten und Wetter in Ostfriesland – mit Karte, Themenwelten und Tipps von Einheimischen.";

const ROUTES = {
  "/": [`${BRAND} – Veranstaltungen, Ausflugsziele & Küste live`, DEFAULT_DESC],
  "/entdecken": ["Ausflugsziele & Orte entdecken", "Über 2.000 Ausflugsziele in Ostfriesland: Strände, Museen, Leuchttürme, Cafés, Restaurants und Natur – filterbar nach Ort und Kategorie."],
  "/entdecken/themen": ["Themenwelten", "E-Bike-Touren, Paddeln im Fehnland, Camping & Wohnmobil, Urlaub mit Hund und barrierefreies Reisen in Ostfriesland."],
  "/entdecken/themen/e-bike": ["Deich-Radtouren mit E-Bike", "Radtouren in Ostfriesland mit GPX-Track, E-Bike-Ladepunkten, Café-Stopps und Wind-Check."],
  "/entdecken/themen/kanu": ["Paddeln im Fehnland – Kanutouren", "Kanu- und SUP-Touren in Ostfriesland mit GPX, Bootsverleihen, Anlegern und Teestuben am Ufer."],
  "/entdecken/themen/camping": ["Camping & Wohnmobil in Ostfriesland", "Stell- und Campingplätze in Ostfriesland mit Strom, Ladesäulen in der Nähe und Hunde-Infos."],
  "/entdecken/themen/hund": ["Urlaub mit Hund in Ostfriesland", "Hundestrände, hundefreundliche Unterkünfte und Restaurants in Ostfriesland auf einen Blick."],
  "/entdecken/themen/barrierefrei": ["Handicap-Kompass Ostfriesland", "Barrierefreie Ausflugsziele und Veranstaltungen in Ostfriesland, zertifiziert nach „Reisen für Alle“."],
  "/wochenende": ["Dieses Wochenende in Ostfriesland", "Was ist am Wochenende in Ostfriesland los? Veranstaltungen Freitag bis Sonntag, Wochenmärkte, Wetter und Gezeiten – jede Woche aktuell."],
  "/veranstaltungen": ["Veranstaltungen in Ostfriesland", "Was ist los in Ostfriesland? Konzerte, Märkte, Feste, Führungen und Familienevents – heute, am Wochenende und danach."],
  "/karte": ["Karte – Ostfriesland auf einen Blick", "Interaktive Karte mit Ausflugszielen, Veranstaltungen, Ladepunkten, Stellplätzen, WCs und mehr in Ostfriesland."],
  "/wetter": ["Wetter, Wind & Wasser an der Küste", "Aktuelles Wetter, Wind, Wassertemperatur, UV, Pollen und Unwetterwarnungen für die ostfriesische Küste."],
  "/gezeiten": ["Gezeiten & Pegel", "Hoch- und Niedrigwasser, Tidenkalender und Pegelstände für die ostfriesische Küste und Inseln."],
  "/inseln": ["Die Ostfriesischen Inseln", "Borkum, Juist, Norderney, Baltrum, Langeoog, Spiekeroog und Wangerooge – mit Fähren, Gezeiten und Tipps."],
  "/orte": ["Orte in Ostfriesland", "Emden, Aurich, Leer, Norden, Greetsiel und mehr – Infos, Veranstaltungen, Wochenmärkte und Tipps von Einheimischen."],
  "/deich-rad": ["Deich & Rad – Gegenwind-Planer", "Radrouten an Deich und Küste mit Gegenwind-Analyse und bestem Zeitfenster."],
  "/badestellen": ["Badestellen & Wasserqualität", "Badestellen an Nordsee und Badeseen in Ostfriesland mit offizieller EU-Wasserqualität."],
  "/anreise": ["Anreise nach Ostfriesland", "Anreise mit Bahn, Auto und Fähre nach Ostfriesland und auf die Inseln."],
  "/merkliste": ["Meine Merkliste", DEFAULT_DESC],
  "/einreichen": ["Veranstaltung einreichen", "Veranstaltung in Ostfriesland kostenlos eintragen – nach kurzer Prüfung sichtbar."],
  "/kontakt": ["Kontakt", "Kontakt zu WattNu Ostfriesland."],
  "/quellen": ["Datenquellen", "Offene Datenquellen und Lizenzen von WattNu Ostfriesland."],
  "/wattfuehrer": ["Wattführungen & Wattführer in Ostfriesland", "Anbieter geführter Wattwanderungen an der ostfriesischen Küste mit Startpunkten und nächsten Terminen – Wattwandern nur mit zertifiziertem Wattführer."],
  "/newsletter": ["Wochenend-Newsletter", DEFAULT_DESC],
};
const PRIVATE = ["/admin", "/konto", "/meine-veranstaltungen", "/sign-in/*", "/sign-up/*", "/newsletter"];
const DETAIL = ["/veranstaltungen/:id", "/orte/:id", "/inseln/:slug", "/entdecken/touren/:id", "/impressum", "/datenschutz", "/nutzungsbedingungen", "/barrierefreiheit"];

const meta = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", value);
};

const CRUMB = { "/entdecken": "Entdecken", "/entdecken/themen": "Themenwelten", "/veranstaltungen": "Veranstaltungen", "/orte": "Orte", "/inseln": "Inseln",
  "/wochenende": "Wochenende", "/wattfuehrer": "Wattführer", "/karte": "Karte", "/wetter": "Wetter", "/gezeiten": "Gezeiten", "/deich-rad": "Deich & Rad",
  "/badestellen": "Badestellen", "/anreise": "Anreise", "/kontakt": "Kontakt", "/quellen": "Datenquellen", "/einreichen": "Veranstaltung einreichen" };

const breadcrumbs = (path, title, crumb, parents) => {
  if (path === "/") return null;
  const segs = path.split("/").filter(Boolean);
  const mid = parents || segs.slice(0, -1).map((_, i) => `/${segs.slice(0, i + 1).join("/")}`).filter((p) => CRUMB[p]).map((p) => [CRUMB[p], p]);
  const items = [["WattNu", "/"], ...mid, [crumb || CRUMB[path] || title.split(/ – | \(/)[0], path]];
  return { "@type": "BreadcrumbList", itemListElement: items.map(([name, p], i) => ({ "@type": "ListItem", position: i + 1, name, item: `${SITE}${p === "/" ? "/" : p}` })) };
};

export const setSeo = ({ title, description = DEFAULT_DESC, image, path = window.location.pathname, noindex = false, jsonLd = null, crumb, crumbParents }) => {
  const withBrand = title.includes(BRAND) ? title : `${title} · ${BRAND}`;
  const full = withBrand.length > 65 && !title.includes(BRAND) ? title : withBrand;
  const url = `${SITE}${path}`;
  const img = image ? (image.startsWith("http") ? image : `${SITE}${image}`) : `${SITE}/og-share.jpg`;
  const desc = description.replace(/\s+/g, " ").trim().slice(0, 160);
  document.title = full;
  meta("name", "description", desc);
  meta("name", "robots", noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large");
  [["og:title", full], ["og:description", desc], ["og:url", url], ["og:image", img]].forEach(([k, v]) => meta("property", k, v));
  [["twitter:title", full], ["twitter:description", desc], ["twitter:image", img]].forEach(([k, v]) => meta("name", k, v));
  let link = document.head.querySelector("link[rel=canonical]");
  if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
  link.href = url;
  document.getElementById("ld-page")?.remove();
  const crumbs = noindex ? null : breadcrumbs(path, title, crumb, crumbParents);
  const graph = [jsonLd, crumbs].filter(Boolean);
  if (graph.length) {
    const s = document.createElement("script");
    s.type = "application/ld+json"; s.id = "ld-page"; s.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(s);
  }
};

export const useSeo = (data, deps) => {
  useEffect(() => { if (data) setSeo(data); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
};

export const RouteSeo = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (DETAIL.some((p) => matchPath(p, pathname))) return;
    const priv = PRIVATE.some((p) => matchPath(p, pathname));
    const [title, description] = ROUTES[pathname] || [BRAND, DEFAULT_DESC];
    setSeo({ title, description, noindex: priv || !ROUTES[pathname] });
  }, [pathname]);
  return null;
};
